import * as THREE from 'three'
import {
  GltfDirectBuildError,
  GltfMemoryBudgetExceededError,
  MeshLoadCancelledError,
} from '@/plugins/mesh/errors'
import { getGltfComponentCount } from '@/plugins/mesh/loaders/gltfLowMemory/accessorLayout'
import { startGltfRuntimePerformanceMonitor } from '@/plugins/mesh/loaders/gltfLowMemory/gltfRuntimePerformance'
import type {
  GltfAccessorReader,
  GltfBufferSource,
  GltfBufferSourcePerformanceReport,
  GltfCoreMaterialFactory,
  GltfDirectGeometryBuilder,
  GltfTaskScheduler,
} from '@/plugins/mesh/loaders/gltfLowMemory/interfaces'
import {
  DEFAULT_GLTF_LOADING_OPTIONS,
  MAX_GLTF_TEXTURE_CONCURRENCY,
} from '@/plugins/mesh/loaders/gltfLowMemory/lowMemoryOptions'
import {
  GltfYieldController,
  MacrotaskGltfScheduler,
} from '@/plugins/mesh/loaders/gltfLowMemory/MacrotaskGltfScheduler'
import type {
  GltfAccessorArray,
  GltfAccessorData,
  GltfBatchPlan,
  GltfBatchPlanEntry,
  GltfComponentType,
  GltfManifestDocument,
  GltfMemoryPlan,
  GltfPrimitiveSource,
} from '@/plugins/mesh/loaders/gltfLowMemory/types'
import { MeshResourceTracker } from '@/plugins/mesh/MeshResourceTracker'
import { MeshBatchSourceMapImpl } from '@/plugins/mesh/optimization/MeshBatchSourceMapImpl'
import type {
  GltfLoaderAdoptedPath,
  GltfTaskYieldOptions,
  MeshBatchSourceRecord,
  MeshLoadDiagnostics,
  MeshLoadRequest,
  MeshLoadResult,
  MeshLoadStats,
  MeshMergedRangeSourceRef,
  MeshPrimitive,
} from '@/plugins/mesh/types'
import { getLoggerManager, LogLevel } from '@/shared/logging'

const buildLog = getLoggerManager().getLogger('mesh:gltf:build')
const textureLog = getLoggerManager().getLogger('mesh:gltf:texture')
const rangeLog = getLoggerManager().getLogger('mesh:gltf:range')
const runtimeLog = getLoggerManager().getLogger('mesh:gltf:runtime')

const ATTRIBUTE_NAMES: Readonly<Record<string, string>> = {
  POSITION: 'position',
  NORMAL: 'normal',
  TANGENT: 'tangent',
  TEXCOORD_0: 'uv',
  TEXCOORD_1: 'uv1',
  COLOR_0: 'color',
}

interface BuiltEntry {
  readonly primitives: readonly MeshPrimitive[]
  readonly records: readonly MeshBatchSourceRecord[]
  readonly instanceBytes: number
}

interface BuildTimings {
  buffersMs: number
  materialsMs: number
}

function resolveMaterialConcurrency(entryCount: number, configuredConcurrency: number): number {
  const normalized = Number.isFinite(configuredConcurrency)
    ? configuredConcurrency
    : DEFAULT_GLTF_LOADING_OPTIONS.texture.maxConcurrentDecodes
  return Math.min(
    entryCount,
    Math.max(1, Math.min(MAX_GLTF_TEXTURE_CONCURRENCY, Math.floor(normalized)))
  )
}

function combineSourceKind(
  left: GltfBufferSourcePerformanceReport['sourceKind'],
  right: GltfBufferSourcePerformanceReport['sourceKind']
): GltfBufferSourcePerformanceReport['sourceKind'] {
  return left === right ? left : 'mixed'
}

function sumRangePerformance(
  sources: readonly GltfBufferSource[]
): GltfBufferSourcePerformanceReport | undefined {
  const reports = sources
    .map((source) => source.getPerformanceReport?.())
    .filter((report): report is GltfBufferSourcePerformanceReport => report !== undefined)
  if (reports.length === 0) return undefined
  return reports.reduce<GltfBufferSourcePerformanceReport>(
    (total, report) => ({
      sourceKind: combineSourceKind(total.sourceKind, report.sourceKind),
      logicalReadCount: total.logicalReadCount + report.logicalReadCount,
      logicalReadBytes: total.logicalReadBytes + report.logicalReadBytes,
      cacheHitCount: total.cacheHitCount + report.cacheHitCount,
      cacheHitBytes: total.cacheHitBytes + report.cacheHitBytes,
      inflightHitCount: total.inflightHitCount + report.inflightHitCount,
      inflightHitBytes: total.inflightHitBytes + report.inflightHitBytes,
      rangeRequestCount: total.rangeRequestCount + report.rangeRequestCount,
      fullRequestCount: total.fullRequestCount + report.fullRequestCount,
      rangeResponseBytes: total.rangeResponseBytes + report.rangeResponseBytes,
      fullResponseBytes: total.fullResponseBytes + report.fullResponseBytes,
      uniqueRangeBytes: total.uniqueRangeBytes + report.uniqueRangeBytes,
      overlapRangeBytes: total.overlapRangeBytes + report.overlapRangeBytes,
      configuredConcurrency: Math.max(total.configuredConcurrency, report.configuredConcurrency),
      peakConcurrentRequests: total.peakConcurrentRequests + report.peakConcurrentRequests,
      peakQueueDepth: total.peakQueueDepth + report.peakQueueDepth,
      queueWaitMs: total.queueWaitMs + report.queueWaitMs,
      queueWaitP95Ms: Math.max(total.queueWaitP95Ms, report.queueWaitP95Ms),
      queueWaitMaxMs: Math.max(total.queueWaitMaxMs, report.queueWaitMaxMs),
      responseWaitMs: total.responseWaitMs + report.responseWaitMs,
      responseWaitP95Ms: Math.max(total.responseWaitP95Ms, report.responseWaitP95Ms),
      responseWaitMaxMs: Math.max(total.responseWaitMaxMs, report.responseWaitMaxMs),
      bodyReadMs: total.bodyReadMs + report.bodyReadMs,
      bodyReadP95Ms: Math.max(total.bodyReadP95Ms, report.bodyReadP95Ms),
      bodyReadMaxMs: Math.max(total.bodyReadMaxMs, report.bodyReadMaxMs),
      bodyCopyMs: total.bodyCopyMs + report.bodyCopyMs,
      bodyCopyMaxMs: Math.max(total.bodyCopyMaxMs, report.bodyCopyMaxMs),
      requestBytesP50: Math.max(total.requestBytesP50, report.requestBytesP50),
      requestBytesP95: Math.max(total.requestBytesP95, report.requestBytesP95),
      requestBytesMax: Math.max(total.requestBytesMax, report.requestBytesMax),
      serverTimingCount: total.serverTimingCount + report.serverTimingCount,
      serverTimingTotalMs: total.serverTimingTotalMs + report.serverTimingTotalMs,
      serverTimingTotalP95Ms: Math.max(total.serverTimingTotalP95Ms, report.serverTimingTotalP95Ms),
      serverTimingTotalMaxMs: Math.max(total.serverTimingTotalMaxMs, report.serverTimingTotalMaxMs),
      serverTimingStatMs: total.serverTimingStatMs + report.serverTimingStatMs,
      serverTimingOpenMs: total.serverTimingOpenMs + report.serverTimingOpenMs,
      serverTimingReadMs: total.serverTimingReadMs + report.serverTimingReadMs,
      serverTimingCloseMs: total.serverTimingCloseMs + report.serverTimingCloseMs,
      networkMs: total.networkMs + report.networkMs,
    }),
    {
      sourceKind: reports[0].sourceKind,
      logicalReadCount: 0,
      logicalReadBytes: 0,
      cacheHitCount: 0,
      cacheHitBytes: 0,
      inflightHitCount: 0,
      inflightHitBytes: 0,
      rangeRequestCount: 0,
      fullRequestCount: 0,
      rangeResponseBytes: 0,
      fullResponseBytes: 0,
      uniqueRangeBytes: 0,
      overlapRangeBytes: 0,
      configuredConcurrency: 0,
      peakConcurrentRequests: 0,
      peakQueueDepth: 0,
      queueWaitMs: 0,
      queueWaitP95Ms: 0,
      queueWaitMaxMs: 0,
      responseWaitMs: 0,
      responseWaitP95Ms: 0,
      responseWaitMaxMs: 0,
      bodyReadMs: 0,
      bodyReadP95Ms: 0,
      bodyReadMaxMs: 0,
      bodyCopyMs: 0,
      bodyCopyMaxMs: 0,
      requestBytesP50: 0,
      requestBytesP95: 0,
      requestBytesMax: 0,
      serverTimingCount: 0,
      serverTimingTotalMs: 0,
      serverTimingTotalP95Ms: 0,
      serverTimingTotalMaxMs: 0,
      serverTimingStatMs: 0,
      serverTimingOpenMs: 0,
      serverTimingReadMs: 0,
      serverTimingCloseMs: 0,
      networkMs: 0,
    }
  )
}

function throwIfCancelled(signal: AbortSignal | undefined, source: string): void {
  if (signal?.aborted) {
    throw new MeshLoadCancelledError(source, { cause: signal.reason })
  }
}

function createAccessorArray(componentType: GltfComponentType, length: number): GltfAccessorArray {
  switch (componentType) {
    case 5120:
      return new Int8Array(length)
    case 5121:
      return new Uint8Array(length)
    case 5122:
      return new Int16Array(length)
    case 5123:
      return new Uint16Array(length)
    case 5125:
      return new Uint32Array(length)
    case 5126:
      return new Float32Array(length)
  }
}

function unionBounds(sources: readonly GltfPrimitiveSource[]): THREE.Box3 {
  const bounds = new THREE.Box3()
  for (const source of sources) {
    if (source.worldBounds) bounds.union(source.worldBounds)
  }
  return bounds
}

function configureObject(object: THREE.Object3D, id: string): void {
  object.name = id
  object.matrixAutoUpdate = false
  object.visible = true
  object.frustumCulled = true
}

function createStats(format: 'gltf' | 'glb', primitives: readonly MeshPrimitive[]): MeshLoadStats {
  const materials = new Set(primitives.map((primitive) => primitive.material.uuid))
  return {
    format,
    primitiveCount: primitives.length,
    triangleCount: primitives.reduce((total, primitive) => total + primitive.triangleCount, 0),
    vertexCount: primitives.reduce((total, primitive) => total + primitive.vertexCount, 0),
    materialCount: materials.size,
    opaqueCount: primitives.filter((primitive) => primitive.renderClass === 'opaque').length,
    maskCount: primitives.filter((primitive) => primitive.renderClass === 'mask').length,
    blendCount: primitives.filter((primitive) => primitive.renderClass === 'blend').length,
  }
}

function calculateGeometryBytes(geometries: ReadonlySet<THREE.BufferGeometry>): number {
  let total = 0
  for (const geometry of geometries) {
    for (const attribute of Object.values(geometry.attributes)) {
      total += attribute.array.byteLength
    }
    total += geometry.getIndex()?.array.byteLength ?? 0
  }
  return total
}

function setIndexUnlessIdentity(
  geometry: THREE.BufferGeometry,
  index: GltfAccessorArray,
  vertexCount: number
): void {
  if (index.length === vertexCount) {
    let identity = true
    for (let item = 0; item < index.length; item += 1) {
      if (index[item] !== item) {
        identity = false
        break
      }
    }
    if (identity) return
  }
  geometry.setIndex(new THREE.BufferAttribute(index, 1))
}

function createSourceMap(
  entries: readonly { primitive: MeshPrimitive; sources: readonly GltfPrimitiveSource[] }[]
): ReadonlyMap<string, readonly MeshPrimitive[]> {
  const result = new Map<string, MeshPrimitive[]>()
  for (const entry of entries) {
    for (const source of entry.sources) {
      const key = `node:${source.nodeIndex}`
      const matches = result.get(key) ?? []
      if (!matches.includes(entry.primitive)) matches.push(entry.primitive)
      result.set(key, matches)
    }
  }
  return result
}

function getAdoptedPath(plan: GltfBatchPlan): GltfLoaderAdoptedPath {
  if (plan.entries.some((entry) => entry.kind === 'merged')) return 'direct-merged'
  if (plan.entries.some((entry) => entry.kind === 'instanced')) return 'direct-instanced'
  return 'direct-shared'
}

function enforceSourceTransientBudget(
  document: GltfManifestDocument,
  memory: GltfMemoryPlan,
  sources: readonly GltfBufferSource[]
): void {
  const peakSourceCacheBytes = Math.max(0, ...sources.map((source) => source.peakCacheBytes))
  const projectedTransientBytes =
    memory.finalGeometryBytes + memory.workspaceBytes + peakSourceCacheBytes
  if (projectedTransientBytes > memory.approvedTransientBytes) {
    throw new GltfMemoryBudgetExceededError(
      document.source,
      'budget-transient-exceeded',
      projectedTransientBytes,
      memory.approvedTransientBytes
    )
  }
}

function getIgnoredAnimations(document: GltfManifestDocument): string[] {
  return (document.manifest.animations ?? []).map((animation, index) => {
    if (
      typeof animation === 'object' &&
      animation !== null &&
      'name' in animation &&
      typeof animation.name === 'string'
    ) {
      return animation.name
    }
    return `animation-${index}`
  })
}

async function copyPosition(
  target: GltfAccessorArray,
  targetOffset: number,
  source: GltfAccessorArray,
  matrix: THREE.Matrix4,
  yieldController: GltfYieldController,
  signal?: AbortSignal
): Promise<void> {
  const elements = matrix.elements
  for (let index = 0; index < source.length; index += 3) {
    const x = source[index]
    const y = source[index + 1]
    const z = source[index + 2]
    const denominator = 1 / (elements[3] * x + elements[7] * y + elements[11] * z + elements[15])
    target[targetOffset + index] =
      (elements[0] * x + elements[4] * y + elements[8] * z + elements[12]) * denominator
    target[targetOffset + index + 1] =
      (elements[1] * x + elements[5] * y + elements[9] * z + elements[13]) * denominator
    target[targetOffset + index + 2] =
      (elements[2] * x + elements[6] * y + elements[10] * z + elements[14]) * denominator
    const pendingYield = yieldController.consume(3 * Float32Array.BYTES_PER_ELEMENT, 1, signal)
    if (pendingYield) await pendingYield
  }
}

async function copyNormal(
  target: GltfAccessorArray,
  targetOffset: number,
  source: GltfAccessorArray,
  matrix: THREE.Matrix4,
  yieldController: GltfYieldController,
  signal?: AbortSignal
): Promise<void> {
  const elements = new THREE.Matrix3().getNormalMatrix(matrix).elements
  for (let index = 0; index < source.length; index += 3) {
    const x = source[index]
    const y = source[index + 1]
    const z = source[index + 2]
    const transformedX = elements[0] * x + elements[3] * y + elements[6] * z
    const transformedY = elements[1] * x + elements[4] * y + elements[7] * z
    const transformedZ = elements[2] * x + elements[5] * y + elements[8] * z
    const inverseLength =
      1 / Math.max(Number.EPSILON, Math.hypot(transformedX, transformedY, transformedZ))
    target[targetOffset + index] = transformedX * inverseLength
    target[targetOffset + index + 1] = transformedY * inverseLength
    target[targetOffset + index + 2] = transformedZ * inverseLength
    const pendingYield = yieldController.consume(3 * Float32Array.BYTES_PER_ELEMENT, 1, signal)
    if (pendingYield) await pendingYield
  }
}

async function copyTangent(
  target: GltfAccessorArray,
  targetOffset: number,
  source: GltfAccessorArray,
  matrix: THREE.Matrix4,
  yieldController: GltfYieldController,
  signal?: AbortSignal
): Promise<void> {
  const elements = matrix.elements
  for (let index = 0; index < source.length; index += 4) {
    const x = source[index]
    const y = source[index + 1]
    const z = source[index + 2]
    const transformedX = elements[0] * x + elements[4] * y + elements[8] * z
    const transformedY = elements[1] * x + elements[5] * y + elements[9] * z
    const transformedZ = elements[2] * x + elements[6] * y + elements[10] * z
    const inverseLength =
      1 / Math.max(Number.EPSILON, Math.hypot(transformedX, transformedY, transformedZ))
    target[targetOffset + index] = transformedX * inverseLength
    target[targetOffset + index + 1] = transformedY * inverseLength
    target[targetOffset + index + 2] = transformedZ * inverseLength
    target[targetOffset + index + 3] = source[index + 3]
    const pendingYield = yieldController.consume(4 * Float32Array.BYTES_PER_ELEMENT, 1, signal)
    if (pendingYield) await pendingYield
  }
}

/** 一次分配最终 TypedArray 的 shared/instanced/merged Geometry 构建器。 */
export class DefaultGltfDirectGeometryBuilder implements GltfDirectGeometryBuilder {
  constructor(
    private readonly accessorReader: GltfAccessorReader,
    private readonly materialFactory: GltfCoreMaterialFactory,
    private readonly scheduler: GltfTaskScheduler = new MacrotaskGltfScheduler(),
    private readonly taskYield: GltfTaskYieldOptions = DEFAULT_GLTF_LOADING_OPTIONS.taskYield,
    private readonly maxConcurrentMaterials = DEFAULT_GLTF_LOADING_OPTIONS.texture
      .maxConcurrentDecodes
  ) {}

  async build(
    document: GltfManifestDocument,
    plan: GltfBatchPlan,
    sources: readonly GltfBufferSource[],
    request: MeshLoadRequest
  ): Promise<MeshLoadResult> {
    const startedAt = getLoggerManager().now()
    const runtimeMonitor = startGltfRuntimePerformanceMonitor(runtimeLog.isEnabled(LogLevel.DEBUG))
    let runtimeLogged = false
    const finishRuntimeMonitoring = (status: 'complete' | 'failed') => {
      if (runtimeLogged) return
      runtimeLogged = true
      const runtime = runtimeMonitor.stop()
      if (!runtime) return
      runtimeLog.debug(
        `event=mesh.gltf.runtime.summary source=${document.source} status=${status} wallMs=${runtime.wallMs.toFixed(
          1
        )} longTaskSupported=${runtime.longTaskSupported} longTasks=${runtime.longTaskCount} longTaskMs=${runtime.longTaskMs.toFixed(
          1
        )} longTaskMaxMs=${runtime.longTaskMaxMs.toFixed(1)} eventLoopSamples=${runtime.eventLoopSampleCount} eventLoopLagMs=${runtime.eventLoopLagMs.toFixed(
          1
        )} eventLoopLagP95Ms=${runtime.eventLoopLagP95Ms.toFixed(1)} eventLoopLagMaxMs=${runtime.eventLoopLagMaxMs.toFixed(
          1
        )} timingKind=wall-clock`
      )
    }
    const signal = request.signal
    const resources = new MeshResourceTracker()
    const primitiveSources: {
      primitive: MeshPrimitive
      sources: readonly GltfPrimitiveSource[]
    }[] = []
    const records: MeshBatchSourceRecord[] = []
    const primitives: MeshPrimitive[] = []
    let instanceBytes = 0
    const timings: BuildTimings = { buffersMs: 0, materialsMs: 0 }
    try {
      const root = new THREE.Group()
      root.name = request.name ?? document.source
      root.matrixAutoUpdate = false
      const materialStartedAt = getLoggerManager().now()
      const materials = await this.preloadMaterials(
        document,
        plan.entries,
        sources,
        resources,
        signal
      )
      timings.materialsMs = getLoggerManager().now() - materialStartedAt
      const rangeDiagnosticsEnabled = rangeLog.isEnabled(LogLevel.DEBUG)
      const textureDiagnosticsEnabled = textureLog.isEnabled(LogLevel.DEBUG)
      const materialRangePerformance = rangeDiagnosticsEnabled
        ? sumRangePerformance(sources)
        : undefined
      for (const [entryIndex, entry] of plan.entries.entries()) {
        throwIfCancelled(signal, document.source)
        const built = await this.buildEntry(
          document,
          entry,
          materials[entryIndex],
          plan.memory,
          sources,
          resources,
          timings,
          signal
        )
        instanceBytes += built.instanceBytes
        records.push(...built.records)
        for (const [primitiveIndex, primitive] of built.primitives.entries()) {
          root.add(primitive.object)
          primitives.push(primitive)
          const mappedSources =
            entry.kind === 'shared' || entry.kind === 'independent'
              ? [entry.sources[primitiveIndex]]
              : entry.sources
          primitiveSources.push({ primitive, sources: mappedSources })
        }
      }
      if (primitives.length === 0) {
        throw new GltfDirectBuildError(document.source, '计划没有生成可渲染 Primitive')
      }

      const boundingBox = new THREE.Box3()
      for (const primitive of primitives) boundingBox.union(primitive.boundingBox)
      const actualAllocatedBytes = calculateGeometryBytes(resources.geometries) + instanceBytes
      if (actualAllocatedBytes > plan.memory.approvedFinalGeometryBytes) {
        throw new GltfMemoryBudgetExceededError(
          document.source,
          'budget-final-exceeded',
          actualAllocatedBytes,
          plan.memory.approvedFinalGeometryBytes
        )
      }
      const actualPeakRangeCacheBytes = Math.max(
        0,
        ...sources.map((source) => source.peakCacheBytes)
      )
      const actualTransientBytes =
        actualAllocatedBytes + actualPeakRangeCacheBytes + plan.memory.workspaceBytes
      if (actualTransientBytes > plan.memory.approvedTransientBytes) {
        throw new GltfMemoryBudgetExceededError(
          document.source,
          'budget-transient-exceeded',
          actualTransientBytes,
          plan.memory.approvedTransientBytes
        )
      }

      const adoptedPath = getAdoptedPath(plan)
      const ignoredAnimations = getIgnoredAnimations(document)
      const diagnostics: MeshLoadDiagnostics = {
        ignoredAnimations,
        warnings:
          ignoredAnimations.length > 0
            ? [`低内存 glTF 路径忽略了 ${ignoredAnimations.length} 个动画`]
            : [],
      }
      const elapsedMs = getLoggerManager().now() - startedAt
      const geometryMs = Math.max(0, elapsedMs - timings.buffersMs - timings.materialsMs)
      const texturePerformance = this.materialFactory.getPerformanceReport?.()
      const actualTextureBytes = texturePerformance?.estimatedTextureBytes ?? 0
      const peakTextureDecodeBytes = texturePerformance?.peakConcurrentImageBytes ?? 0
      const estimatedPeakWorkingSetBytes =
        actualAllocatedBytes +
        actualTextureBytes +
        actualPeakRangeCacheBytes +
        peakTextureDecodeBytes +
        plan.memory.workspaceBytes
      const rangePerformance = rangeDiagnosticsEnabled ? sumRangePerformance(sources) : undefined
      if (rangePerformance) {
        const materialRequests = materialRangePerformance
          ? materialRangePerformance.rangeRequestCount + materialRangePerformance.fullRequestCount
          : 0
        const materialDownloadedBytes = materialRangePerformance
          ? materialRangePerformance.rangeResponseBytes + materialRangePerformance.fullResponseBytes
          : 0
        const totalRequests = rangePerformance.rangeRequestCount + rangePerformance.fullRequestCount
        const totalDownloadedBytes =
          rangePerformance.rangeResponseBytes + rangePerformance.fullResponseBytes
        const bodyThroughputMbps =
          rangePerformance.bodyReadMs > 0
            ? (totalDownloadedBytes * 8) / rangePerformance.bodyReadMs / 1000
            : 0
        rangeLog.debug(
          `event=mesh.gltf.range.summary source=${document.source} sourceKind=${rangePerformance.sourceKind} sources=${sources.length} logicalReads=${rangePerformance.logicalReadCount} logicalBytes=${rangePerformance.logicalReadBytes} networkRequests=${
            totalRequests
          } rangeRequests=${rangePerformance.rangeRequestCount} fullRequests=${rangePerformance.fullRequestCount} downloadedBytes=${
            totalDownloadedBytes
          } uniqueRangeBytes=${rangePerformance.uniqueRangeBytes} overlapBytes=${rangePerformance.overlapRangeBytes} cacheHits=${rangePerformance.cacheHitCount} cacheHitBytes=${rangePerformance.cacheHitBytes} inflightHits=${rangePerformance.inflightHitCount} inflightHitBytes=${rangePerformance.inflightHitBytes} networkCumulativeMs=${rangePerformance.networkMs.toFixed(
            1
          )} rangeConcurrency=${rangePerformance.configuredConcurrency} peakRangeConcurrency=${rangePerformance.peakConcurrentRequests} peakQueueDepth=${rangePerformance.peakQueueDepth} queueWaitMs=${rangePerformance.queueWaitMs.toFixed(
            1
          )} queueWaitP95Ms=${rangePerformance.queueWaitP95Ms.toFixed(1)} queueWaitMaxMs=${rangePerformance.queueWaitMaxMs.toFixed(
            1
          )} responseWaitMs=${rangePerformance.responseWaitMs.toFixed(1)} responseWaitP95Ms=${rangePerformance.responseWaitP95Ms.toFixed(
            1
          )} responseWaitMaxMs=${rangePerformance.responseWaitMaxMs.toFixed(1)} bodyReadMs=${rangePerformance.bodyReadMs.toFixed(
            1
          )} bodyReadP95Ms=${rangePerformance.bodyReadP95Ms.toFixed(1)} bodyReadMaxMs=${rangePerformance.bodyReadMaxMs.toFixed(
            1
          )} bodyCopyMs=${rangePerformance.bodyCopyMs.toFixed(1)} bodyCopyMaxMs=${rangePerformance.bodyCopyMaxMs.toFixed(
            1
          )} requestBytesP50=${rangePerformance.requestBytesP50} requestBytesP95=${rangePerformance.requestBytesP95} requestBytesMax=${rangePerformance.requestBytesMax} bodyThroughputMbps=${bodyThroughputMbps.toFixed(
            2
          )} serverTimingCount=${rangePerformance.serverTimingCount} serverTimingTotalMs=${rangePerformance.serverTimingTotalMs.toFixed(
            1
          )} serverTimingTotalP95Ms=${rangePerformance.serverTimingTotalP95Ms.toFixed(
            1
          )} serverTimingTotalMaxMs=${rangePerformance.serverTimingTotalMaxMs.toFixed(
            1
          )} serverTimingStatMs=${rangePerformance.serverTimingStatMs.toFixed(
            1
          )} serverTimingOpenMs=${rangePerformance.serverTimingOpenMs.toFixed(
            1
          )} serverTimingReadMs=${rangePerformance.serverTimingReadMs.toFixed(
            1
          )} serverTimingCloseMs=${rangePerformance.serverTimingCloseMs.toFixed(
            1
          )} materialRequests=${materialRequests} materialDownloadedBytes=${materialDownloadedBytes} materialQueueWaitMs=${(
            materialRangePerformance?.queueWaitMs ?? 0
          ).toFixed(1)} materialResponseWaitMs=${(
            materialRangePerformance?.responseWaitMs ?? 0
          ).toFixed(1)} materialBodyReadMs=${(materialRangePerformance?.bodyReadMs ?? 0).toFixed(
            1
          )} geometryRequests=${Math.max(0, totalRequests - materialRequests)} geometryDownloadedBytes=${Math.max(
            0,
            totalDownloadedBytes - materialDownloadedBytes
          )} geometryQueueWaitMs=${Math.max(
            0,
            rangePerformance.queueWaitMs - (materialRangePerformance?.queueWaitMs ?? 0)
          ).toFixed(1)} geometryResponseWaitMs=${Math.max(
            0,
            rangePerformance.responseWaitMs - (materialRangePerformance?.responseWaitMs ?? 0)
          ).toFixed(1)} geometryBodyReadMs=${Math.max(
            0,
            rangePerformance.bodyReadMs - (materialRangePerformance?.bodyReadMs ?? 0)
          ).toFixed(1)} peakCacheBytes=${Math.max(
            0,
            ...sources.map((source) => source.peakCacheBytes)
          )} timingKind=cumulative-requests`
        )
      }
      if (textureDiagnosticsEnabled && texturePerformance && texturePerformance.imageCount > 0) {
        const effectiveConcurrency = resolveMaterialConcurrency(
          plan.entries.length,
          this.maxConcurrentMaterials
        )
        const imageWorkerMs = texturePerformance.imageReadMs + texturePerformance.imageDecodeMs
        const averageActiveImageWorkers =
          timings.materialsMs > 0 ? imageWorkerMs / timings.materialsMs : 0
        const imageWorkerUtilization =
          effectiveConcurrency > 0 ? averageActiveImageWorkers / effectiveConcurrency : 0
        textureLog.debug(
          `event=mesh.gltf.texture.summary source=${document.source} images=${
            texturePerformance.imageCount
          } embeddedImages=${texturePerformance.embeddedImageCount} externalImages=${texturePerformance.externalImageCount} dataUriImages=${texturePerformance.dataUriImageCount} decoder=${texturePerformance.decoderKind} encodedBytes=${texturePerformance.encodedImageBytes} readMs=${texturePerformance.imageReadMs.toFixed(
            1
          )} readP50Ms=${texturePerformance.imageReadP50Ms.toFixed(1)} readP95Ms=${texturePerformance.imageReadP95Ms.toFixed(
            1
          )} readMaxMs=${texturePerformance.imageReadMaxMs.toFixed(1)} decodeMs=${texturePerformance.imageDecodeMs.toFixed(
            1
          )} decodeP50Ms=${texturePerformance.imageDecodeP50Ms.toFixed(1)} decodeP95Ms=${texturePerformance.imageDecodeP95Ms.toFixed(
            1
          )} decodeMaxMs=${texturePerformance.imageDecodeMaxMs.toFixed(1)} headerMs=${texturePerformance.headerMs.toFixed(
            1
          )} directDecodeMs=${texturePerformance.directDecodeMs.toFixed(1)} fullDecodeMs=${texturePerformance.fullDecodeMs.toFixed(
            1
          )} resizeMs=${texturePerformance.resizeMs.toFixed(1)} directResize=${
            texturePerformance.directResizeCount
          } fallbackResize=${texturePerformance.fallbackResizeCount} unchanged=${
            texturePerformance.unchangedCount
          } slowestImage=${texturePerformance.slowestImageIndex ?? -1} slowestDecodeMs=${texturePerformance.slowestImageDecodeMs.toFixed(
            1
          )} slowestBytes=${texturePerformance.slowestImageBytes} slowestOutput=${
            texturePerformance.slowestImageWidth
          }x${texturePerformance.slowestImageHeight} configuredConcurrency=${this.maxConcurrentMaterials} effectiveConcurrency=${effectiveConcurrency} peakReadConcurrency=${texturePerformance.peakConcurrentImageReads} peakDecodeConcurrency=${texturePerformance.peakConcurrentImages} averageActiveImageWorkers=${averageActiveImageWorkers.toFixed(
            2
          )} imageWorkerUtilizationPct=${(imageWorkerUtilization * 100).toFixed(1)} estimatedTextureBytes=${texturePerformance.estimatedTextureBytes} peakDecodeBytes=${texturePerformance.peakConcurrentImageBytes} estimatedGpuBytes=${
            actualAllocatedBytes + actualTextureBytes
          } estimatedPeakWorkingSetBytes=${estimatedPeakWorkingSetBytes} timingKind=cumulative-workers`
        )
      }
      const mergedPrimitiveCount = plan.entries
        .filter((entry) => entry.kind === 'merged')
        .reduce((total, entry) => total + entry.sources.length, 0)
      const instancedPrimitiveCount = plan.entries
        .filter((entry) => entry.kind === 'instanced')
        .reduce((total, entry) => total + entry.sources.length, 0)
      finishRuntimeMonitoring('complete')
      return {
        root,
        primitives,
        format: document.format,
        resources,
        diagnostics,
        boundingBox,
        sourcePrimitiveMap: createSourceMap(primitiveSources),
        batchSourceMap: new MeshBatchSourceMapImpl(records),
        consumedOptimization: plan.consumedOptimization,
        report: {
          stats: createStats(document.format, primitives),
          diagnostics,
          elapsedMs,
          adoptedPath,
          lowMemory: {
            adoptedPath,
            reasonCode: plan.reasonCode,
            memory: {
              ...plan.memory,
              actualAllocatedBytes,
              peakRangeCacheBytes: actualPeakRangeCacheBytes,
              additionalDownloadedBytes: sources.reduce(
                (total, source) => total + source.downloadedBytes,
                0
              ),
              actualTextureBytes,
              peakTextureDecodeBytes,
              estimatedPeakWorkingSetBytes,
              peakConcurrentTextureDecodes: texturePerformance?.peakConcurrentImages ?? 0,
            },
            timings: {
              manifestMs: 0,
              planningMs: 0,
              buffersMs: timings.buffersMs,
              geometryMs,
              materialsMs: timings.materialsMs,
              fallbackMs: 0,
              totalMs: elapsedMs,
            },
            diagnostics: [],
          },
          optimization: {
            status: mergedPrimitiveCount + instancedPrimitiveCount > 0 ? 'optimized' : 'skipped',
            beforeBatchCount: plan.entries.reduce(
              (total, entry) => total + entry.sources.length,
              0
            ),
            afterBatchCount: primitives.length,
            instancedBatchCount: plan.entries.filter((entry) => entry.kind === 'instanced').length,
            instancedPrimitiveCount,
            mergedBatchCount: plan.entries.filter((entry) => entry.kind === 'merged').length,
            mergedPrimitiveCount,
            skippedPrimitiveCount: plan.entries
              .filter((entry) => entry.kind === 'shared' || entry.kind === 'independent')
              .reduce((total, entry) => total + entry.sources.length, 0),
            drawCallReduction: Math.max(
              0,
              plan.entries.reduce((total, entry) => total + entry.sources.length, 0) -
                primitives.length
            ),
            timings: {
              partitionMs: 0,
              batchingMs: elapsedMs,
              finalizeMs: 0,
              totalMs: elapsedMs,
            },
            appliedAt: 'loader',
            reasonCode: plan.reasonCode,
            memory: {
              ...plan.memory,
              actualAllocatedBytes,
            },
          },
        },
        dispose: () => resources.dispose(),
      }
    } catch (error) {
      finishRuntimeMonitoring('failed')
      buildLog.debug(
        `event=mesh.gltf.build.failed source=${document.source} elapsedMs=${(
          getLoggerManager().now() - startedAt
        ).toFixed(1)} reason=${error instanceof Error ? error.message : String(error)}`
      )
      try {
        resources.dispose()
      } catch {
        // 保留直接构建原始错误。
      }
      throw error
    }
  }

  private async buildEntry(
    document: GltfManifestDocument,
    entry: GltfBatchPlanEntry,
    material: THREE.Material,
    memory: GltfMemoryPlan,
    sources: readonly GltfBufferSource[],
    resources: MeshResourceTracker,
    timings: BuildTimings,
    signal?: AbortSignal
  ): Promise<BuiltEntry> {
    enforceSourceTransientBudget(document, memory, sources)
    if (entry.kind === 'merged') {
      return this.buildMerged(
        document,
        entry,
        memory,
        sources,
        resources,
        material,
        timings,
        signal
      )
    }
    const geometry = resources.trackGeometry(
      await this.buildLocalGeometry(document, entry.sources[0], memory, sources, timings, signal)
    )
    if (entry.kind === 'instanced') {
      const object = new THREE.InstancedMesh(geometry, material, entry.sources.length)
      configureObject(object, entry.id)
      entry.sources.forEach((source, instanceId) => {
        object.setMatrixAt(instanceId, source.worldMatrix)
      })
      object.instanceMatrix.needsUpdate = true
      object.computeBoundingBox()
      object.computeBoundingSphere()
      const primitive = this.createPrimitive(
        entry.id,
        entry.sources[0],
        object,
        geometry,
        material,
        new THREE.Matrix4(),
        unionBounds(entry.sources),
        entry.sources.reduce((total, source) => total + source.vertexCount, 0),
        entry.sources.reduce((total, source) => total + source.indexCount / 3, 0)
      )
      return {
        primitives: [primitive],
        records: [
          {
            kind: 'instanced',
            batchId: entry.id,
            instances: entry.sources.map((source, instanceId) => ({
              instanceId,
              primitiveId: source.id,
              sourceNodeId: `node:${source.nodeIndex}`,
            })),
          },
        ],
        instanceBytes: object.instanceMatrix.array.byteLength,
      }
    }

    const primitives = entry.sources.map((source) => {
      const object = new THREE.Mesh(geometry, material)
      configureObject(object, source.id)
      object.matrix.copy(source.worldMatrix)
      object.matrixWorld.copy(source.worldMatrix)
      return this.createPrimitive(
        source.id,
        source,
        object,
        geometry,
        material,
        source.worldMatrix.clone(),
        source.worldBounds?.clone() ?? new THREE.Box3(),
        source.vertexCount,
        source.indexCount / 3
      )
    })
    return { primitives, records: [], instanceBytes: 0 }
  }

  private async preloadMaterials(
    document: GltfManifestDocument,
    entries: readonly GltfBatchPlanEntry[],
    sources: readonly GltfBufferSource[],
    resources: MeshResourceTracker,
    signal?: AbortSignal
  ): Promise<THREE.Material[]> {
    const materials = new Array<THREE.Material>(entries.length)
    const concurrency = resolveMaterialConcurrency(entries.length, this.maxConcurrentMaterials)
    let nextIndex = 0
    let firstError: unknown
    const workers = Array.from({ length: concurrency }, async () => {
      while (firstError === undefined) {
        const index = nextIndex
        if (index >= entries.length) return
        nextIndex += 1
        const entry = entries[index]
        const attributes = entry.sources[0].attributeAccessors
        try {
          throwIfCancelled(signal, document.source)
          materials[index] = resources.trackMaterial(
            await this.materialFactory.createMaterial(
              document,
              entry.sources[0].materialIndex,
              sources,
              signal,
              {
                vertexColors: Object.hasOwn(attributes, 'COLOR_0'),
                flatShading: !Object.hasOwn(attributes, 'NORMAL'),
              }
            )
          )
        } catch (error) {
          firstError ??= error
        }
      }
    })
    await Promise.all(workers)
    if (firstError !== undefined) throw firstError
    return materials
  }

  private async buildLocalGeometry(
    document: GltfManifestDocument,
    source: GltfPrimitiveSource,
    memory: GltfMemoryPlan,
    sources: readonly GltfBufferSource[],
    timings: BuildTimings,
    signal?: AbortSignal
  ): Promise<THREE.BufferGeometry> {
    const geometry = new THREE.BufferGeometry()
    for (const [semantic, accessorIndex] of Object.entries(source.attributeAccessors)) {
      const name = ATTRIBUTE_NAMES[semantic]
      if (!name) throw new GltfDirectBuildError(document.source, `不支持 attribute ${semantic}`)
      const startedAt = getLoggerManager().now()
      const data = await this.accessorReader.read(document, accessorIndex, sources, signal)
      timings.buffersMs += getLoggerManager().now() - startedAt
      try {
        enforceSourceTransientBudget(document, memory, sources)
        geometry.setAttribute(
          name,
          new THREE.BufferAttribute(data.array, data.layout.componentCount, data.layout.normalized)
        )
      } finally {
        this.accessorReader.release?.(document, accessorIndex)
      }
    }
    if (source.indexAccessor !== undefined) {
      const startedAt = getLoggerManager().now()
      const index = await this.accessorReader.read(document, source.indexAccessor, sources, signal)
      timings.buffersMs += getLoggerManager().now() - startedAt
      try {
        enforceSourceTransientBudget(document, memory, sources)
        setIndexUnlessIdentity(geometry, index.array, source.vertexCount)
      } finally {
        this.accessorReader.release?.(document, source.indexAccessor)
      }
    }
    geometry.computeBoundingBox()
    geometry.computeBoundingSphere()
    return geometry
  }

  private async buildMerged(
    document: GltfManifestDocument,
    entry: GltfBatchPlanEntry,
    memory: GltfMemoryPlan,
    sources: readonly GltfBufferSource[],
    resources: MeshResourceTracker,
    material: THREE.Material,
    timings: BuildTimings,
    signal?: AbortSignal
  ): Promise<BuiltEntry> {
    const geometry = resources.trackGeometry(new THREE.BufferGeometry())
    const yieldController = new GltfYieldController(this.scheduler, this.taskYield)
    const first = entry.sources[0]
    const totalVertices = entry.sources.reduce((total, source) => total + source.vertexCount, 0)
    for (const [semantic, firstAccessorIndex] of Object.entries(first.attributeAccessors)) {
      const name = ATTRIBUTE_NAMES[semantic]
      if (!name) throw new GltfDirectBuildError(document.source, `不支持 attribute ${semantic}`)
      const accessor = document.manifest.accessors?.[firstAccessorIndex]
      if (!accessor) {
        throw new GltfDirectBuildError(document.source, `accessors[${firstAccessorIndex}] 不存在`)
      }
      const componentCount = getGltfComponentCount(accessor.type)
      const target = createAccessorArray(accessor.componentType, totalVertices * componentCount)
      let vertexOffset = 0
      for (const source of entry.sources) {
        const accessorIndex = source.attributeAccessors[semantic]
        const startedAt = getLoggerManager().now()
        const data = await this.accessorReader.read(document, accessorIndex, sources, signal)
        timings.buffersMs += getLoggerManager().now() - startedAt
        try {
          enforceSourceTransientBudget(document, memory, sources)
          await this.writeMergedAttribute(
            semantic,
            target,
            vertexOffset * data.layout.componentCount,
            data,
            source.worldMatrix,
            document.source,
            yieldController,
            signal
          )
          vertexOffset += data.layout.count
        } finally {
          this.accessorReader.release?.(document, accessorIndex)
        }
      }
      geometry.setAttribute(
        name,
        new THREE.BufferAttribute(target, componentCount, accessor.normalized ?? false)
      )
    }

    const indexed = first.indexAccessor !== undefined
    const ranges: MeshMergedRangeSourceRef[] = []
    if (indexed) {
      const totalIndices = entry.sources.reduce((total, source) => total + source.indexCount, 0)
      const index =
        totalVertices > 65_535 ? new Uint32Array(totalIndices) : new Uint16Array(totalIndices)
      let indexOffset = 0
      let vertexOffset = 0
      for (const source of entry.sources) {
        const accessorIndex = source.indexAccessor!
        const startedAt = getLoggerManager().now()
        const data = await this.accessorReader.read(document, accessorIndex, sources, signal)
        timings.buffersMs += getLoggerManager().now() - startedAt
        try {
          enforceSourceTransientBudget(document, memory, sources)
          for (let item = 0; item < data.array.length; item += 1) {
            index[indexOffset + item] = data.array[item] + vertexOffset
          }
          ranges.push({
            primitiveId: source.id,
            sourceNodeId: `node:${source.nodeIndex}`,
            start: indexOffset,
            count: data.array.length,
          })
          indexOffset += data.array.length
          vertexOffset += source.vertexCount
        } finally {
          this.accessorReader.release?.(document, accessorIndex)
        }
      }
      setIndexUnlessIdentity(geometry, index, totalVertices)
    } else {
      let vertexOffset = 0
      for (const source of entry.sources) {
        ranges.push({
          primitiveId: source.id,
          sourceNodeId: `node:${source.nodeIndex}`,
          start: vertexOffset,
          count: source.vertexCount,
        })
        vertexOffset += source.vertexCount
      }
    }
    geometry.computeBoundingBox()
    geometry.computeBoundingSphere()

    const object = new THREE.Mesh(geometry, material)
    configureObject(object, entry.id)
    object.matrix.identity()
    object.matrixWorld.identity()
    const triangleCount = entry.sources.reduce((total, source) => total + source.indexCount / 3, 0)
    const primitive = this.createPrimitive(
      entry.id,
      first,
      object,
      geometry,
      material,
      new THREE.Matrix4(),
      unionBounds(entry.sources),
      totalVertices,
      triangleCount
    )
    return {
      primitives: [primitive],
      records: [{ kind: 'merged', batchId: entry.id, ranges }],
      instanceBytes: 0,
    }
  }

  private async writeMergedAttribute(
    semantic: string,
    target: GltfAccessorArray,
    targetOffset: number,
    data: GltfAccessorData,
    matrix: THREE.Matrix4,
    source: string,
    yieldController: GltfYieldController,
    signal?: AbortSignal
  ): Promise<void> {
    if (semantic === 'POSITION') {
      if (data.layout.componentType !== 5126 || data.layout.componentCount !== 3) {
        throw new GltfDirectBuildError(source, 'POSITION 直接变换要求 FLOAT VEC3')
      }
      await copyPosition(target, targetOffset, data.array, matrix, yieldController, signal)
      return
    }
    if (semantic === 'NORMAL') {
      if (data.layout.componentType !== 5126 || data.layout.componentCount !== 3) {
        throw new GltfDirectBuildError(source, 'NORMAL 直接变换要求 FLOAT VEC3')
      }
      await copyNormal(target, targetOffset, data.array, matrix, yieldController, signal)
      return
    }
    if (semantic === 'TANGENT') {
      if (data.layout.componentType !== 5126 || data.layout.componentCount !== 4) {
        throw new GltfDirectBuildError(source, 'TANGENT 直接变换要求 FLOAT VEC4')
      }
      await copyTangent(target, targetOffset, data.array, matrix, yieldController, signal)
      return
    }
    target.set(data.array, targetOffset)
    const pendingYield = yieldController.consume(data.array.byteLength, data.layout.count, signal)
    if (pendingYield) await pendingYield
  }

  private createPrimitive(
    id: string,
    source: GltfPrimitiveSource,
    object: THREE.Mesh,
    geometry: THREE.BufferGeometry,
    material: THREE.Material,
    worldMatrix: THREE.Matrix4,
    boundingBox: THREE.Box3,
    vertexCount: number,
    triangleCount: number
  ): MeshPrimitive {
    return {
      id,
      sourceNodeId: `node:${source.nodeIndex}`,
      sourceNodeName: source.nodeName,
      object,
      geometry,
      material,
      worldMatrix,
      boundingBox,
      renderClass: source.renderClass,
      triangleCount,
      vertexCount,
    }
  }
}
